import { City as CityLookup } from "country-state-city";
import { COUNTRIES } from "../views/pages/StationRequests/countryData";

export type WorldCityOption = {
	id: string;
	label: string;
	countryCode: string;
	countryName: string;
	cityName: string;
	lat?: number;
	long?: number;
	/** Bản ghi GeoNames (server) nếu có */
	geonameId?: number;
	timezone?: string;
};

/**
 * Bỏ dấu, chữ hoa/thường — dùng bước trước khi bỏ khoảng.
 */
function normalizeSearch(s: string) {
	return s
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/đ/g, "d")
		.replace(/Đ/g, "D")
		.toLowerCase();
}

/**
 * Tìm kiếm mềm: không dấu, gộp/ bỏ mọi khoảng, không phân biệt hoa thường
 * (ví dụ "phuquoc" ↔ "Phú Quốc", "MADRID" ↔ "madrid").
 */
export function normalizeLooseSearchString(s: string) {
	return normalizeSearch(s).replace(/\s+/g, "");
}

function stripVnHuyenPrefix(name: string) {
	return name.replace(/^Huyện\s+/i, "").replace(/^Huyen\s+/i, "").trim();
}

/**
 * Các biến thể tên để so khớp: tên gốc + (VN) tên bỏ tiền tố Huyện để vẫn tìm được
 * "phu quoc" dù bản ghi thư viện là "Huyện Phú Quốc".
 */
function cityNameMatchVariants(cityName: string, countryCode: string) {
	const out: string[] = [cityName];
	if (countryCode === "VN") {
		const s = stripVnHuyenPrefix(cityName);
		if (s && s !== cityName) out.push(s);
	}
	return out;
}

function matches(nqLoose: string, cityName: string, countryName: string, countryCode: string) {
	const countryL = normalizeLooseSearchString(countryName);
	if (countryL.includes(nqLoose)) return true;
	for (const cvar of cityNameMatchVariants(cityName, countryCode)) {
		const cityL = normalizeLooseSearchString(cvar);
		if (cityL.includes(nqLoose)) return true;
		if (normalizeLooseSearchString(`${cvar} ${countryName}`).includes(nqLoose)) return true;
	}
	return false;
}

/**
 * country-state-city mô tả cả cấp huyện/thị dưới tỉnh dưới tên "city" (gây nhiễu cho chọn
 * destination tầng tỉnh/TP). Bỏ qua khi tên đã rõ là cấp huyện (Vietnam: Huyện / Huyen...).
 */
export function shouldIncludeWorldLibraryPlaceName(cityName: string, countryCode: string): boolean {
	const t = String(cityName || "").trim();
	if (!t) return false;
	if (countryCode === "VN") {
		// 351+ mục bắt đầu bằng "Huyện …" / "Huyen …" trong bản dữ liệu VN
		if (/^Huyện\s/i.test(t) || /^Huyen\s/i.test(t)) {
			return false;
		}
	}
	return true;
}

/**
 * Duyệt toàn bộ thành phố theo từng nước (không cắt danh sách theo nước);
 * `maxResults` giới hạn tổng số dòng (UI). So khớp: không dấu, bỏ khoảng, hoa thường tùy ý.
 */
export function searchWorldLibraryCities(query: string, maxResults = 100): WorldCityOption[] {
	const raw = String(query || "").trim();
	if (raw.length < 2) return [];
	const nq = normalizeLooseSearchString(raw);
	if (!nq) return [];
	const out: WorldCityOption[] = [];
	for (const c of COUNTRIES) {
		if (out.length >= maxResults) break;
		const cities = CityLookup.getCitiesOfCountry(c.code) || [];
		for (const city of cities) {
			if (out.length >= maxResults) break;
			if (!matches(nq, city.name, c.name, c.code)) continue;
			// Tên bị loại ở world picker (Huyện) — chỉ thêm nếu query trúng phần sau "Huyện …" (vd. phuquoc)
			if (!shouldIncludeWorldLibraryPlaceName(city.name, c.code)) {
				if (c.code !== "VN" || !/^Huyện\s|^Huyen\s/i.test(city.name)) continue;
				const rest = stripVnHuyenPrefix(city.name);
				if (!rest || !normalizeLooseSearchString(rest).includes(nq)) continue;
			}
			const rawLat = city.latitude;
			const rawLong = city.longitude;
			const lat = rawLat != null && rawLat !== "" ? parseFloat(String(rawLat)) : undefined;
			const long = rawLong != null && rawLong !== "" ? parseFloat(String(rawLong)) : undefined;
			const displayCity =
				c.code === "VN" && stripVnHuyenPrefix(city.name) && /^Huyện\s|^Huyen\s/i.test(city.name)
					? stripVnHuyenPrefix(city.name)
					: city.name;
			out.push({
				id: `${c.code}::${city.name}`,
				label: `${displayCity}, ${c.name}`,
				countryCode: c.code,
				countryName: c.name,
				cityName: city.name,
				lat: Number.isFinite(lat) ? lat : undefined,
				long: Number.isFinite(long) ? long : undefined,
			});
		}
	}
	return out;
}
